import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  RefreshCw, 
  Bot, 
  Megaphone, 
  GraduationCap, 
  ShieldCheck, 
  TrendingUp,
  ArrowRight
} from 'lucide-react';

const ApexFullService = () => {
  const navigate = useNavigate();

  const goToApplication = () => {
    navigate('/5k-guarantee#application');
  };

  const services = [
    {
      id: 'service-reactivation',
      icon: RefreshCw,
      title: 'Reactivation Campaigns',
      description: 'Stellar, super-effective campaigns that bring back lapsed members and lost leads. Turn your dormant database into revenue.'
    },
    {
      id: 'service-ai-chatbot',
      icon: Bot,
      title: 'AI Lead Nurturing',
      description: 'Smart chatbots that nurture leads who do not convert instantly or you cannot reach straight away. Never let a lead go cold again.'
    },
    {
      id: 'service-marketing',
      icon: Megaphone,
      title: 'Cutting-Edge Marketing',
      description: 'The latest campaigns that are working right now across the country. We bring you what is actually converting, not last year\'s tactics.'
    },
    {
      id: 'service-training',
      icon: GraduationCap,
      title: 'Sales Training & Seminars',
      description: 'Remote and in-person training sessions tailored specifically to your team. Upskill your entire operation, not just one person.'
    },
    {
      id: 'service-retention',
      icon: ShieldCheck,
      title: 'Retention Mastery',
      description: 'Proven techniques we use with our clients to keep member churn under 2%. Stop the leaky bucket and focus purely on growth.'
    },
    {
      id: 'service-growth',
      icon: TrendingUp,
      title: 'Full Growth Strategy',
      description: 'End-to-end support from lead generation to long-term retention. We are your complete marketing and growth partner.'
    }
  ];

  return (
    <section className="relative py-20 md:py-28 bg-[#0d0d0d] overflow-hidden">
      {/* Background effects */}
      <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-[#22C55E]/5 rounded-full blur-[150px]" />
      <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-[#22C55E]/5 rounded-full blur-[120px]" />
      
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="text-[#22C55E] text-sm font-medium tracking-[0.2em] uppercase">
            More Than Just Sales
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mt-4 mb-4">
            Your Complete Growth Partner
          </h2>
          <p className="text-[#A1A1AA] max-w-3xl mx-auto text-lg">
            We are not just sales coaches — we are <span className="text-white font-semibold">marketing and growth specialists</span>. 
            Everything we do is tailored specifically to generate more revenue for your business.
          </p>
        </div>

        {/* Services grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => (
            <div
              key={service.id}
              data-testid={service.id}
              className="group bg-[#121212] border border-[#27272A] rounded-2xl p-6 hover:border-[#22C55E]/50 transition-all duration-300 relative overflow-hidden"
            >
              {/* Hover glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#22C55E]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              
              <div className="relative">
                {/* Icon */}
                <div className="w-12 h-12 rounded-xl bg-[#22C55E]/10 flex items-center justify-center mb-4 group-hover:bg-[#22C55E]/20 transition-colors">
                  <service.icon className="w-6 h-6 text-[#22C55E]" />
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-white mb-2">
                  {service.title}
                </h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">
                  {service.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom highlight box */}
        <div className="mt-12 bg-gradient-to-r from-[#121212] via-[#0d1f12] to-[#121212] border border-[#22C55E]/20 rounded-2xl p-8 md:p-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="text-center lg:text-left">
              <h3 className="text-2xl font-bold text-white mb-2">
                Under 2% Churn. Consistent Growth.
              </h3>
              <p className="text-[#A1A1AA] max-w-xl">
                We implement the same techniques across all our partner gyms to minimise member loss. 
                When you are not constantly replacing churned members, you can focus entirely on scaling.
              </p>
            </div>
            <button
              onClick={goToApplication}
              data-testid="full-service-cta"
              className="group inline-flex items-center justify-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] text-black font-bold uppercase tracking-wide rounded-full px-8 py-4 whitespace-nowrap transition-all duration-300 hover:-translate-y-0.5"
            >
              See If You Qualify
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApexFullService;
